import { Link } from 'react-router-dom';
import { useT } from '@/i18n/useT';
import { errorMessage } from '@/lib/errorMessage';
import { ROUTES } from '@/lib/config';
import { useBotState, useResetLadder, useResetDay, useSwitchAccount } from '@/bot/hooks';
import { useLinkStatus } from '@/tenancy/hooks';
import { BotGate } from './BotGate';
import { BotNav } from './BotNav';
import { ConfirmButton } from './ConfirmButton';
import { CommunityCard } from './CommunityCard';

/**
 * Bot settings: the operations that reset state, plus the Pocket Option
 * connection.
 *
 * Both resets are confirmed because both are visible in money terms — resetting
 * the ladder abandons an in-progress Martingale cycle, and resetting the day
 * clears the counters the stop-loss and profit target are measured against,
 * which re-arms a bot that had deliberately stopped for the day.
 */
export function BotSettingsPage() {
  const { t } = useT();
  const state = useBotState();
  const link = useLinkStatus();
  const resetLadder = useResetLadder();
  const resetDay = useResetDay();
  const switchAccount = useSwitchAccount();

  return (
    <BotGate query={state}>
      {(s) => {
        const isReal = s.control.activeAccountType === 'real';
        return (
        <>
          <section className="bot-card">
            <div className="bot-card-head"><h2>{t('botset.connection')}</h2></div>
            <div className="bot-account">
              <strong>{link.data?.poEmail ?? t('common.none')}</strong>
              <span className="muted">
                {link.data?.state === 'linked' ? t('bot.connectedBlurb') : (link.data?.detail ?? '')}
              </span>
            </div>
            <Link to={ROUTES.bot} className="bot-btn ghost">{t('billing.manageCta')}</Link>
          </section>

          {/* Demo <-> real. The single most consequential control in the app,
              so it is confirmed, states plainly that real money is involved,
              and warns that the switch takes up to a minute — the bot has to
              load a different Pocket Option cabinet and re-read the balance. */}
          <section className={`bot-card ${isReal ? 'bot-real' : ''}`}>
            <div className="bot-card-head">
              <h2>{t('botset.accountMode')}</h2>
              <span className={isReal ? 'down strong' : 'up strong'}>
                {isReal ? t('bot.real') : t('bot.demo')}
              </span>
            </div>
            <p className="muted">{isReal ? t('dash.realMode') : t('dash.demoMode')}</p>
            {isReal ? <p className="bot-alert error">{t('botset.realWarning')}</p> : null}

            {/* Feedback the approved settings tab gave and this screen did not:
                a failed switch (the bot answers 409 with a reason) was thrown
                away and nothing changed on screen; a successful one said
                nothing either. Both are the exact "switch hung" experience. */}
            {switchAccount.isError ? (
              <p className="bot-alert error">{errorMessage(switchAccount.error)}</p>
            ) : null}
            {switchAccount.isSuccess && !s.control.accountSwitchPending ? (
              <p className="bot-alert ok">{t('botset.switched', { mode: isReal ? t('bot.real') : t('bot.demo') })}</p>
            ) : null}
            {switchAccount.isPending ? (
              <p className="muted">{t('botset.switching')}</p>
            ) : (
              <>
                {s.control.accountSwitchPending ? (
                  <p className="muted">{t('botset.switchPendingHint')}</p>
                ) : null}
                <ConfirmButton
                  label={isReal ? t('botset.switchToDemo') : t('botset.switchToReal')}
                  confirmText={isReal ? t('botset.confirmDemo') : t('botset.confirmReal')}
                  busy={switchAccount.isPending}
                  onConfirm={() => switchAccount.mutate(isReal ? 'demo' : 'real')}
                  className={isReal ? 'bot-btn ghost' : 'bot-btn danger'}
                />
              </>
            )}
          </section>

          <section className="bot-card">
            <div className="bot-card-head"><h2>{t('botset.controls')}</h2></div>

            <div className="bot-action">
              <div>
                <strong>{t('botset.resetLadder')}</strong>
                <span className="muted">{t('botset.resetLadderSub')}</span>
              </div>
              <ConfirmButton
                label={t('botset.resetLadder')}
                confirmText={t('botset.confirmResetLadder')}
                busy={resetLadder.isPending}
                onConfirm={() => resetLadder.mutate()}
                className="bot-btn ghost"
              />
            </div>

            <div className="bot-action">
              <div>
                <strong>{t('botset.resetDay')}</strong>
                <span className="muted">{t('botset.resetDaySub')}</span>
              </div>
              <ConfirmButton
                label={t('botset.resetDay')}
                confirmText={t('botset.confirmResetDay')}
                busy={resetDay.isPending}
                onConfirm={() => resetDay.mutate()}
                className="bot-btn ghost"
              />
            </div>
          </section>

          <section className="bot-card">
            <div className="bot-card-head"><h2>{t('dash.quickStats')}</h2></div>
            <dl className="bot-stats">
              <div><dt>{t('dash.step')}</dt><dd>{s.step}</dd></div>
              <div><dt>{t('dash.nextStake')}</dt><dd>${s.nextStake.toFixed(2)}</dd></div>
              <div><dt>{t('strategy.payout')}</dt><dd>{s.control.payoutThresholdPct}%</dd></div>
              <div><dt>{t('strategy.rateLimit')}</dt><dd>{s.control.rateLimit.tradesPerHour || '∞'}</dd></div>
            </dl>
          </section>

          {/* Official Telegram channel + community group. */}
          <CommunityCard />

          <BotNav />
        </>
        );
      }}
    </BotGate>
  );
}
